import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Loader2, Film, Download, Share2, Copy, Check, AlertCircle, Sparkles } from "lucide-react";
import api, { API, formatApiErrorDetail, getStoredToken } from "@/lib/api";
import { toast } from "sonner";

const MUSIC_OPTIONS = [
    { value: "wedding", label: "Wedding · soft + ceremonial" },
    { value: "festive", label: "Festive · upbeat traditional" },
    { value: "romantic", label: "Romantic · gentle ambient" },
    { value: "royal", label: "Royal · regal classical" },
    { value: "modern", label: "Modern · contemporary chill" },
    { value: "silent", label: "No music" },
];

export default function ReelMakerDialog({ saree, open, onOpenChange }) {
    const [music, setMusic] = useState("wedding");
    const [fmt, setFmt] = useState("vertical");
    const [duration, setDuration] = useState(10);
    const [captionMode, setCaptionMode] = useState("ai");
    const [manualCaption, setManualCaption] = useState("");
    const [reel, setReel] = useState(null);
    const [working, setWorking] = useState(false);
    const [copiedCaption, setCopiedCaption] = useState(false);

    useEffect(() => {
        if (!open) {
            setReel(null); setWorking(false); setCopiedCaption(false);
            setManualCaption(""); setCaptionMode("ai");
        }
    }, [open]);

    if (!saree) return null;

    const generate = async () => {
        setWorking(true);
        setReel(null);
        try {
            const { data } = await api.post(`/admin/sarees/${saree.id}/reel`, {
                music, fmt, duration,
                caption_mode: captionMode,
                manual_caption: manualCaption,
            });
            // Poll
            const start = Date.now();
            while (Date.now() - start < 90_000) {
                await new Promise((r) => setTimeout(r, 2500));
                const { data: r } = await api.get(`/admin/reels/${data.id}`);
                if (r.status === "done") {
                    setReel(r);
                    toast.success("Reel ready!");
                    return;
                }
                if (r.status === "failed") {
                    toast.error(r.error || "Reel generation failed");
                    return;
                }
            }
            toast.error("Reel generation took too long — please retry");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to start reel");
        } finally {
            setWorking(false);
        }
    };

    const downloadUrl = reel ? `${API}/admin/reels/${reel.id}/download` : null;

    const downloadFile = async () => {
        if (!reel) return;
        try {
            const token = getStoredToken();
            const res = await fetch(downloadUrl, {
                credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) throw new Error("Download failed");
            const blob = await res.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `vastra-reel-${reel.id.slice(0, 8)}.mp4`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(a.href);
        } catch {
            toast.error("Could not download — try again");
        }
    };

    const shareInstagram = async () => {
        // First copy the caption + open Instagram. iOS / Android will let user paste in the post composer.
        if (reel?.caption) {
            try { await navigator.clipboard.writeText(reel.caption); } catch {}
        }
        // Try the Web Share API first (works on most mobile browsers)
        try {
            const token = getStoredToken();
            const res = await fetch(downloadUrl, {
                credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            const blob = await res.blob();
            const file = new File([blob], `reel-${reel.id.slice(0, 8)}.mp4`, { type: "video/mp4" });
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({ files: [file], text: reel.caption || "" });
                toast.success("Caption copied — paste in Instagram!");
                return;
            }
        } catch {}
        // Fallback: download + open Instagram URL
        await downloadFile();
        toast.success("Reel downloaded — open Instagram to post");
        window.open("instagram://library", "_blank") ||
            window.open("https://www.instagram.com/", "_blank");
    };

    const shareWhatsApp = async () => {
        if (reel?.caption) {
            try { await navigator.clipboard.writeText(reel.caption); } catch {}
        }
        try {
            const token = getStoredToken();
            const res = await fetch(downloadUrl, {
                credentials: "include",
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            const blob = await res.blob();
            const file = new File([blob], `reel-${reel.id.slice(0, 8)}.mp4`, { type: "video/mp4" });
            if (navigator.canShare && navigator.canShare({ files: [file] })) {
                await navigator.share({ files: [file], text: reel.caption || "" });
                return;
            }
        } catch {}
        await downloadFile();
        const text = encodeURIComponent(reel?.caption || "");
        window.open(`https://wa.me/?text=${text}`, "_blank");
    };

    const copyCaption = async () => {
        if (!reel?.caption) return;
        try {
            await navigator.clipboard.writeText(reel.caption);
            setCopiedCaption(true);
            toast.success("Caption copied");
            setTimeout(() => setCopiedCaption(false), 1500);
        } catch {
            toast.error("Could not copy");
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-brand-paper border-brand-border max-w-xl max-h-[92vh] overflow-y-auto" data-testid="reel-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl flex items-center gap-2">
                        <Film className="w-5 h-5 text-brand-primary" strokeWidth={1.5} />
                        Make a reel
                    </DialogTitle>
                </DialogHeader>
                <p className="text-brand-muted text-sm mt-1">{saree.title}</p>

                {!reel ? (
                    <div className="mt-5 space-y-4">
                        <div>
                            <label className="ayraa-overline">Music</label>
                            <select className="ayraa-input mt-1.5" value={music} onChange={(e) => setMusic(e.target.value)} data-testid="reel-music-select">
                                {MUSIC_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                            <p className="text-xs text-brand-soft mt-1">Tip: silent placeholders ship by default — drop your own MP3s in <code>backend/assets/music/</code> to use real tracks.</p>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="ayraa-overline">Format</label>
                                <select className="ayraa-input mt-1.5" value={fmt} onChange={(e) => setFmt(e.target.value)} data-testid="reel-fmt-select">
                                    <option value="vertical">9:16 (Reels / Stories)</option>
                                    <option value="square">1:1 (Feed post)</option>
                                </select>
                            </div>
                            <div>
                                <label className="ayraa-overline">Duration</label>
                                <select className="ayraa-input mt-1.5" value={duration} onChange={(e) => setDuration(parseInt(e.target.value, 10))} data-testid="reel-duration-select">
                                    <option value={6}>6 seconds</option>
                                    <option value={10}>10 seconds</option>
                                    <option value={15}>15 seconds</option>
                                    <option value={20}>20 seconds</option>
                                    <option value={30}>30 seconds</option>
                                </select>
                            </div>
                        </div>

                        <div>
                            <label className="ayraa-overline">Caption</label>
                            <div className="flex gap-2 mt-1.5">
                                <button
                                    type="button"
                                    className={`px-3 py-1.5 text-xs rounded-md border ${captionMode === "ai" ? "bg-brand-primary text-white border-brand-primary" : "bg-brand-paper border-brand-border"}`}
                                    onClick={() => setCaptionMode("ai")}
                                    data-testid="reel-caption-ai"
                                >
                                    <Sparkles className="w-3 h-3 inline mr-1" /> AI auto
                                </button>
                                <button
                                    type="button"
                                    className={`px-3 py-1.5 text-xs rounded-md border ${captionMode === "manual" ? "bg-brand-primary text-white border-brand-primary" : "bg-brand-paper border-brand-border"}`}
                                    onClick={() => setCaptionMode("manual")}
                                    data-testid="reel-caption-manual"
                                >
                                    Type my own
                                </button>
                            </div>
                            {captionMode === "manual" ? (
                                <textarea
                                    className="ayraa-input mt-2 min-h-[80px]"
                                    placeholder="Caption + hashtags…"
                                    value={manualCaption}
                                    onChange={(e) => setManualCaption(e.target.value)}
                                    data-testid="reel-manual-caption"
                                />
                            ) : null}
                        </div>

                        <button
                            className="ayraa-btn-primary w-full"
                            disabled={working}
                            onClick={generate}
                            data-testid="reel-generate-btn"
                        >
                            {working ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating reel…</>
                            ) : (
                                <><Sparkles className="w-4 h-4 mr-2" /> Generate reel</>
                            )}
                        </button>
                    </div>
                ) : (
                    <div className="mt-5 space-y-4" data-testid="reel-preview">
                        <video
                            controls
                            autoPlay
                            playsInline
                            className="w-full rounded-md bg-black aspect-[9/16] sm:aspect-video"
                            src={`${downloadUrl}?token=${encodeURIComponent(getStoredToken() || "")}`}
                        >
                            <source src={`${downloadUrl}?token=${encodeURIComponent(getStoredToken() || "")}`} type="video/mp4" />
                        </video>

                        <div className="ayraa-card p-3">
                            <div className="flex items-start justify-between gap-2">
                                <div className="text-xs text-brand-soft uppercase tracking-wider">Caption</div>
                                <button onClick={copyCaption} className="text-xs text-brand-primary flex items-center gap-1" data-testid="reel-copy-caption">
                                    {copiedCaption ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                                    {copiedCaption ? "Copied" : "Copy"}
                                </button>
                            </div>
                            <p className="text-sm whitespace-pre-wrap mt-1">{reel.caption}</p>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            <button onClick={shareInstagram} className="ayraa-btn-primary flex-1" data-testid="reel-share-ig">
                                <Share2 className="w-4 h-4 mr-1.5" /> Open in Instagram
                            </button>
                            <button onClick={shareWhatsApp} className="ayraa-btn-ghost flex-1" data-testid="reel-share-wa">
                                <Share2 className="w-4 h-4 mr-1.5" /> WhatsApp
                            </button>
                            <button onClick={downloadFile} className="ayraa-btn-ghost flex-1" data-testid="reel-download">
                                <Download className="w-4 h-4 mr-1.5" /> Download
                            </button>
                        </div>

                        <button
                            className="text-xs text-brand-muted hover:text-brand-primary mx-auto block"
                            onClick={() => setReel(null)}
                            data-testid="reel-make-another"
                        >
                            ← Tweak settings &amp; regenerate
                        </button>
                    </div>
                )}

                <div className="mt-4 text-xs text-brand-soft flex items-start gap-1.5">
                    <AlertCircle className="w-3 h-3 shrink-0 mt-0.5" />
                    <span>Reels stay on the server for ~24h. Re-share later by regenerating — same source photos = no extra cost.</span>
                </div>
            </DialogContent>
        </Dialog>
    );
}
